'use client'
import { useState } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { useLanguage } from '@/lib/context/LanguageContext'
import SectionHeader from '@/components/ui/SectionHeader'
import ContactForm from '@/components/sections/ContactForm'
import CallbackForm from '@/components/sections/CallbackForm'

const EASE = [0.65, 0, 0.35, 1] as const

/**
 * Closing chapter. Full maroon background, cream typography.
 *
 * Primary CTA: phone-only callback request. The clinic leaves a number and
 * MRN initiates — inverts the usual "you reach out" pattern in a way that
 * matches the brand's selective regional-distributor posture.
 *
 * Secondary path: a collapsed full form for visitors who want to share more
 * context up front. Opens inline when "Or send a detailed message" is
 * clicked — never the dominant action.
 */
export default function ContactCTA() {
  const { t, isRTL } = useLanguage()
  const [showForm, setShowForm] = useState(false)
  const reduce = useReducedMotion()

  return (
    <section
      id="contact"
      className="relative bg-accent-deep py-32 sm:py-48"
      style={{ direction: isRTL ? 'rtl' : 'ltr' }}
    >
      <div className="mx-auto max-w-2xl px-5 sm:px-8 text-center">
        <SectionHeader
          rule
          heading={t.contact.heading}
          sub={t.contact.sub}
          align="center"
          light
        />

        <CallbackForm />

        <div className="mt-10 border-t border-surface/15 pt-8">
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            aria-expanded={showForm}
            className="cursor-pointer text-lg sm:text-xl font-medium text-surface underline underline-offset-4 decoration-surface/40 hover:decoration-surface transition-colors"
          >
            {showForm ? t.contact.callbackCollapse : t.contact.callbackExpand}
          </button>
        </div>

        <AnimatePresence initial={false}>
          {showForm && (
            <motion.div
              key="detailed-form"
              initial={reduce ? false : { height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
              transition={{ duration: reduce ? 0.15 : 0.4, ease: EASE }}
              className="overflow-hidden"
            >
              <div className="pt-8 text-start" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                <ContactForm />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  )
}
